import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { rsFromTo } from '../../shared/motion/rs-motion';

export type SvcIcon =
  | 'code'
  | 'monitor'
  | 'mobile'
  | 'megaphone'
  | 'search'
  | 'headset';

export interface Svc {
  name: string;
  /** One sentence, two lines maximum. */
  desc: string;
  link: string;
  icon: SvcIcon;
  /** Where it sits in the asymmetric layout. */
  slot: 'a' | 'b' | 'c' | 'd' | 'wide';
}

let registered = false;

@Component({
  selector: 'app-services',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './services.component.html',
  styleUrls: ['./services.component.scss'],
})
export class ServicesComponent implements AfterViewInit, OnDestroy {
  /** Anchor id. Configurable so two pages can host this without
      colliding on a duplicate DOM id. */
  @Input() anchor = 'services';

  @ViewChild('root') root?: ElementRef<HTMLElement>;
  @ViewChild('head') head?: ElementRef<HTMLElement>;
  @ViewChild('title') title?: ElementRef<HTMLElement>;
  @ViewChild('grid') grid?: ElementRef<HTMLElement>;
  @ViewChild('spot') spot?: ElementRef<HTMLElement>;
  @ViewChildren('card', { read: ElementRef })
  cardEls?: QueryList<ElementRef<HTMLElement>>;

  reduced = false;

  /** The featured card — kept separate, it owns its own layout. */
  readonly featured = {
    name: 'Custom Software Development',
    desc: 'Tailored software solutions designed to solve your unique business challenges.',
    link: '/service/software-Development',
    cta: 'Explore Solutions',
  };

  /**
   * The five supporting services.
   * IT Consulting has no service page yet, so it routes to contact.
   */
  readonly items: Svc[] = [
    {
      name: 'Web Design & Development',
      desc: 'Responsive, user-friendly websites that drive engagement and growth.',
      link: '/service/web-development',
      icon: 'monitor',
      slot: 'a',
    },
    {
      name: 'Digital Marketing',
      desc: 'Strategic marketing solutions to reach your audience and increase conversions.',
      link: '/service/digital-Media-Marketing',
      icon: 'megaphone',
      slot: 'b',
    },
    {
      name: 'Mobile App Development',
      desc: 'High-performance mobile and desktop applications that streamline your processes.',
      link: '/service/android-app-development',
      icon: 'mobile',
      slot: 'c',
    },
    {
      name: 'IT Consulting',
      desc: 'Expert guidance to help you make smarter technology decisions.',
      link: '/contact-us',
      icon: 'headset',
      slot: 'd',
    },
    {
      name: 'SEO Optimization',
      desc: 'Expert optimization strategies to improve your visibility and rank higher in search results.',
      link: '/service/search-Engine-Optimisation',
      icon: 'search',
      slot: 'wide',
    },
  ];

  private ctx?: gsap.Context;
  private split?: SplitText;
  private cleanups: Array<() => void> = [];
  private isBrowser: boolean;

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  trackSvc = (_i: number, s: Svc) => s.name;

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) return;

    if (!registered) {
      gsap.registerPlugin(ScrollTrigger, SplitText);
      registered = true;
    }

    this.zone.runOutsideAngular(() => {
      this.ctx = gsap.context(() => {
        this.buildHeader();
        this.buildCards();
      }, this.host.nativeElement);

      this.bindSpotlight();
      this.bindCardLight();
    });
  }

  /* ------------------------------------------------------------------
     HEADER — fade up, heading resolving line by line
     ------------------------------------------------------------------ */
  private buildHeader(): void {
    const titleEl = this.title?.nativeElement;

    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      scrollTrigger: {
        trigger: this.head!.nativeElement,
        start: 'top 84%',
        once: true,
      },
    });

    tl.fromTo('.svc__badge', ...rsFromTo({ opacity: 0, y: 16, duration: 0.5 }));

    if (titleEl) {
      /* Split into lines. The gradient sits on the words themselves —
         background-clip on a wrapper breaks once SplitText re-nests. */
      this.split = new SplitText(titleEl, {
        type: 'lines',
        linesClass: 'svc__tline',
      });
      tl.fromTo(
        this.split.lines, ...rsFromTo({ opacity: 0, yPercent: 104, duration: 0.78, stagger: 0.1 }),
        '-=0.26'
      );
    }

    tl.fromTo('.svc__lede', ...rsFromTo({ opacity: 0, y: 14, duration: 0.55 }), '-=0.42');
  }

  /* ------------------------------------------------------------------
     CARDS — stagger reveal, 0.08s apart
     ------------------------------------------------------------------ */
  private buildCards(): void {
    const cards = (this.cardEls?.toArray() ?? []).map((r) => r.nativeElement);
    if (!cards.length) return;

    gsap.fromTo(cards, ...rsFromTo({
      opacity: 0,
      y: 50,
      scale: 0.95,
      duration: 0.8,
      ease: 'power3.out',
      stagger: 0.08,
      scrollTrigger: {
        trigger: this.grid!.nativeElement,
        start: 'top 86%',
        once: true,
      },
    }));
  }

  /* ------------------------------------------------------------------
     SECTION SPOTLIGHT — a soft radial light trailing the cursor
     ------------------------------------------------------------------ */
  private bindSpotlight(): void {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const root = this.root?.nativeElement;
    const spot = this.spot?.nativeElement;
    if (!fine || !root || !spot) return;

    const x = gsap.quickTo(spot, 'x', { duration: 0.9, ease: 'power3.out' });
    const y = gsap.quickTo(spot, 'y', { duration: 0.9, ease: 'power3.out' });

    let queued = false;
    let px = 0;
    let py = 0;

    const apply = () => {
      queued = false;
      x(px);
      y(py);
    };

    const onMove = (e: MouseEvent) => {
      const r = root.getBoundingClientRect();
      px = e.clientX - r.left;
      py = e.clientY - r.top;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    const onEnter = () => gsap.to(spot, { opacity: 1, duration: 0.5 });
    const onLeave = () => gsap.to(spot, { opacity: 0, duration: 0.6 });

    root.addEventListener('mousemove', onMove, { passive: true });
    root.addEventListener('mouseenter', onEnter, { passive: true });
    root.addEventListener('mouseleave', onLeave, { passive: true });

    this.cleanups.push(() => {
      root.removeEventListener('mousemove', onMove);
      root.removeEventListener('mouseenter', onEnter);
      root.removeEventListener('mouseleave', onLeave);
    });
  }

  /* ------------------------------------------------------------------
     PER-CARD LIGHT — the sheen that follows the cursor inside a card.
     Written as CSS variables so the paint stays on the compositor.
     ------------------------------------------------------------------ */
  private bindCardLight(): void {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;

    (this.cardEls?.toArray() ?? []).forEach(({ nativeElement: el }) => {
      let queued = false;
      let mx = '50%';
      let my = '50%';

      const apply = () => {
        queued = false;
        el.style.setProperty('--mx', mx);
        el.style.setProperty('--my', my);
      };

      const onMove = (e: MouseEvent) => {
        const r = el.getBoundingClientRect();
        mx = `${((e.clientX - r.left) / r.width) * 100}%`;
        my = `${((e.clientY - r.top) / r.height) * 100}%`;
        if (queued) return;
        queued = true;
        requestAnimationFrame(apply);
      };

      el.addEventListener('mousemove', onMove, { passive: true });
      this.cleanups.push(() => el.removeEventListener('mousemove', onMove));
    });
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.split?.revert();
    this.ctx?.revert();
  }
}
